import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { 
  Calculator, 
  ArrowDownCircle,
  ArrowUpCircle,
  FileCheck,
  Calendar,
  MessageSquare,
  Users,
  CreditCard,
  PlusCircle,
  TrendingUp,
  Briefcase,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';

const AccountingPanelContent = () => {
  const location = useLocation();
  const { getBadgeCount, markSeen } = useBadges();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Bildirimler alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname, fetchUnreadCounts]);

  const menuItems = [
    { path: '/accounting/income', icon: ArrowDownCircle, label: 'Girişler', menuKey: 'income', badgeKey: 'accounting_girisler' },
    { path: '/accounting/expenses', icon: ArrowUpCircle, label: 'Çıkışlar', menuKey: 'expenses', badgeKey: 'accounting_cikislar' },
    { path: '/accounting/faturalar', icon: FileText, label: 'Faturalar', menuKey: 'invoices', badgeKey: null },
    { path: '/accounting/takipler', icon: TrendingUp, label: 'Takipler', menuKey: 'tracking', badgeKey: 'accounting_takipler' },
    { path: '/accounting/checks', icon: FileCheck, label: 'Çekler', menuKey: 'checks', badgeKey: null },
    { path: '/accounting/cari-hesaplar', icon: Users, label: 'Cari Hesaplar', menuKey: 'accounts', badgeKey: null },
    { path: '/accounting/hesap-kartlari', icon: CreditCard, label: 'Hesap Kartları', menuKey: 'cards', badgeKey: null },
    { path: '/accounting/hesap-ekle', icon: PlusCircle, label: 'Hesaplar', menuKey: 'add_account', badgeKey: null },
    { path: '/accounting/calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
    { path: '/accounting/jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: 'accounting_jobs' },
    { path: '/accounting/communication', icon: MessageSquare, label: 'İletişim', menuKey: 'communication', badgeKey: 'accounting_inbox' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="accounting-panel">
      {/* Header */}
      <PanelHeader
        title="Muhasebe Paneli"
        titleIcon={Calculator}
        titleColor="text-indigo-600"
      />

      <div className="flex pt-16">
        {/* Collapsible Sidebar */}
        <aside 
          className={`fixed top-16 left-0 h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white overflow-y-auto z-40 transition-all duration-300 ${
            sidebarExpanded ? 'w-48' : 'w-14'
          }`}
          onMouseEnter={() => setSidebarExpanded(true)}
          onMouseLeave={() => setSidebarExpanded(false)}
        >
          <nav className={`space-y-1 ${sidebarExpanded ? 'p-4' : 'p-2'}`}>
            {menuItems.map((item) => {
              // Badge sayısını belirle - önce yeni sistemden
              let badgeCount = getBadgeCount(item.menuKey);
              if (badgeCount === 0 && item.badgeKey && unreadCounts[item.badgeKey] > 0) {
                badgeCount = unreadCounts[item.badgeKey];
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => item.menuKey && markSeen(item.menuKey)}
                  className={`relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(item.path)
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  } ${!sidebarExpanded ? 'justify-center px-2' : ''}`}
                  title={!sidebarExpanded ? item.label : undefined}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {sidebarExpanded && (
                    <>
                      <span>{item.label}</span>
                      {badgeCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center">
                          {badgeCount}
                        </Badge>
                      )}
                    </>
                  )}
                  {!sidebarExpanded && badgeCount > 0 && (
                    <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 text-[10px] flex items-center justify-center">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
            
            {/* Uyarılar butonu */}
            <Link
              to="/accounting/warnings"
              className={`relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/accounting/warnings')
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-amber-50 hover:text-amber-700'
              } ${!sidebarExpanded ? 'justify-center px-2' : ''}`}
              title={!sidebarExpanded ? 'Uyarılar' : undefined}
              data-testid="menu-warnings"
            >
              <AlertTriangle className="h-5 w-5 shrink-0" />
              {sidebarExpanded && <span>Uyarılar</span>}
            </Link>
          </nav>
        </aside>

        {/* Main Content - with left margin for fixed sidebar */}
        <main className={`flex-1 p-6 overflow-x-auto transition-all duration-300 ${sidebarExpanded ? 'ml-48' : 'ml-14'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const AccountingPanel = () => {
  return (
    <BadgeProvider panel="accounting">
      <AccountingPanelContent />
    </BadgeProvider>
  );
};

export default AccountingPanel;
